/**
 * Greybox materials only — no tone art packs.
 * Flat, readable greys/blues for path, units, mecha placeholders.
 */
import * as THREE from "three";
export const GREYBOX = {
    pathDeck: 0x6b7280,
    pathRail: 0x4b5563,
    pathEdge: 0x9ca3af,
    /** Concrete guardrail beam / posts (slightly cooler than deck). */
    guardrail: 0x5b6470,
    /** Painted center lane line (ad bridge read). */
    laneLine: 0xe5e7eb,
    /** Faint city skyline silhouettes in the fog. */
    skyline: 0x1a2332,
    /** Blue uniform body (ad-faithful soldier read). */
    unit: 0x60a5fa,
    unitEmissive: 0x1e3a5f,
    /** Darker kit backpack on soldier back. */
    soldierBackpack: 0x1e3a5f,
    /** Held weapon / rifle silhouette. */
    soldierWeapon: 0x374151,
    /** Head / skin block for low-poly soldier. */
    soldierSkin: 0xd4a574,
    /**
     * Bare-skin zombie palette (ad-faithful horde) — pale greens / greys / flesh.
     * No uniform, no weapon kit colors.
     */
    zombieFlesh: 0x9aaa7a,
    zombieFleshEmissive: 0x2a3320,
    zombieLimb: 0x7a8a6a,
    zombieHead: 0xb8c4a0,
    mecha: 0x94a3b8,
    mechaAccent: 0xcbd5e1,
    ground: 0x1f2937,
    /** Atmospheric distance fog / clear color (matches scene.fog). */
    fog: 0x111827,
};
export function makePathDeckMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.pathDeck,
        roughness: 0.85,
        metalness: 0.05,
        flatShading: true,
    });
}
export function makePathRailMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.pathRail,
        roughness: 0.9,
        metalness: 0.0,
        flatShading: true,
    });
}
/** Concrete guardrail rails + posts (ad bridge edges). */
export function makeGuardrailMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.guardrail,
        roughness: 0.92,
        metalness: 0.02,
        flatShading: true,
    });
}
/** Painted dashed center lane line on the bridge deck. */
export function makeLaneLineMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.laneLine,
        roughness: 0.75,
        metalness: 0,
        flatShading: true,
        // Slight lift above deck so it reads without z-fight
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
    });
}
/** Faint skyline silhouettes — dark, unlit-ish, dissolve into fog. */
export function makeSkylineMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.skyline,
        roughness: 1,
        metalness: 0,
        flatShading: true,
        // No emissive — pure silhouette in atmospheric fog
    });
}
export function makeUnitMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.unit,
        emissive: GREYBOX.unitEmissive,
        emissiveIntensity: 0.15,
        roughness: 0.7,
        metalness: 0.1,
        flatShading: true,
    });
}
/** Darker backpack box on soldier back (reads as kit, not uniform). */
export function makeSoldierBackpackMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.soldierBackpack,
        roughness: 0.85,
        metalness: 0.05,
        flatShading: true,
    });
}
/** Rifle / held-weapon silhouette (dark metal grey). */
export function makeSoldierWeaponMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.soldierWeapon,
        roughness: 0.55,
        metalness: 0.45,
        flatShading: true,
    });
}
/** Skin tone for low-poly soldier head. */
export function makeSoldierSkinMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.soldierSkin,
        roughness: 0.8,
        metalness: 0,
        flatShading: true,
    });
}
/** Bare-skin zombie flesh (pale green-grey — no uniform). */
export function makeZombieFleshMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.zombieFlesh,
        emissive: GREYBOX.zombieFleshEmissive,
        emissiveIntensity: 0.12,
        roughness: 0.92,
        metalness: 0,
        flatShading: true,
    });
}
/** Darker zombie limb / rag flesh variation. */
export function makeZombieLimbMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.zombieLimb,
        roughness: 0.95,
        metalness: 0,
        flatShading: true,
    });
}
/** Sickly zombie head / face block. */
export function makeZombieHeadMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.zombieHead,
        roughness: 0.88,
        metalness: 0,
        flatShading: true,
    });
}
export function makeMechaMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.mecha,
        roughness: 0.55,
        metalness: 0.25,
        flatShading: true,
    });
}
export function makeGroundMaterial() {
    return new THREE.MeshStandardMaterial({
        color: GREYBOX.ground,
        roughness: 1,
        metalness: 0,
        flatShading: true,
    });
}
//# sourceMappingURL=materials.js.map